import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DriverService from '../../../services/driver/driver-service';
import { commonHandlePageChange, commonHandleSorting } from '../../../utils/commonFunctions';
import { handleErrorMessage } from '../../../utils/helper';
import { IDriverSubscription, IDriverSubscriptionList } from '../../../models/driver';
import StandardResponse from '../../../models/standard-response';
import { IUserDetail } from '../../../models/user';
import { IModalTypeWithCallBack } from '../../../types/stateTypes/stateTypes';
import { IDriverSubscriptionsProps } from '../../../types/propTypes';

export const useDriverSubscriptionGrid = (props: IDriverSubscriptionsProps) => {
    const params = useParams();
    const [subscriptionList, setSubscriptionList] = useState<Array<IDriverSubscription>>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [modal, setModal] = useState<IModalTypeWithCallBack>({
        showModal: false,
        header: '',
        body: '',
        callBack: null,
    });
    const [driverName, setDriverName] = useState<string>('');

    const filterState = props.filterState;

    const driverService = new DriverService();

    const fetchData = async () => {
        let driverName = '';
        const driverID = parseInt(params.driverId!);
        if (driverID && driverID > 0) {
            const driverDetails = await getDriverDetails(driverID);
            driverName = driverDetails && driverDetails.full_name ? driverDetails.full_name : '';
        }
        setDriverName(driverName);
    };

    const getDriverSubscriptionData = async () => {
        let subscriptionList: Array<IDriverSubscription> = [];

        const filterInput = {
            limit: filterState.rowsPerPage,
            offset: filterState.offset,
            order: [[filterState.sortColumn, filterState.sortOrder]],
        };
        const driverID = parseInt(params.driverId!);
        if (driverID && driverID > 0) {
            Object.assign(filterInput, {
                driver_id: driverID,
            });

            await driverService
                .getDriverSubscriptionList(filterInput)
                .then(async (res: StandardResponse<IDriverSubscriptionList>) => {
                    subscriptionList = res.Data.rows;
                    setTotalCount(res.Data.count);
                })
                .catch((m) => {
                    handleErrorMessage(m);
                });
        }
        setSubscriptionList(subscriptionList);
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        getDriverSubscriptionData();
    }, [filterState]);

    const handleModal = (body: string, header: string, callBack: Function | null) => {
        setModal({
            body: body,
            header: header,
            callBack: callBack,
            showModal: !modal.showModal,
        });
    };

    const cancelDriverSubscription = async (driver_premium_subscription_id: number, is_terminate: boolean) => {
        const inputData = {
            is_terminate,
        };
        await driverService
            .cancelSubscription(driver_premium_subscription_id, inputData)
            .then(async (res: StandardResponse<number>) => {
                toast.success(res.Messages);
            })
            .catch((m) => {
                handleErrorMessage(m);
            });
        setModal({
            showModal: false,
            header: '',
            body: '',
            callBack: null,
        });
        props.setFilterState((prevValue) => ({
            ...prevValue,
        }));
    };

    const handlePageChange = async (currentPage: number, rowsPerPage: number) => {
        commonHandlePageChange(currentPage, rowsPerPage, props.setFilterState);
    };

    const handleSorting = async (columnName: string) => {
        commonHandleSorting(columnName, filterState, props.setFilterState);
    };

    const getDriverDetails = async (id: number) => {
        let driverDetails;
        await driverService
            .getDriverDetail(id)
            .then(async (res: StandardResponse<IUserDetail>) => {
                driverDetails = res.Data;
            })
            .catch((m) => {
                toast.error(m.toString());
            })
            .finally();
        return driverDetails;
    };

    return {
        subscriptionList,
        totalCount,
        modal,
        driverName,
        fetchData,
        getDriverSubscriptionData,
        handleModal,
        cancelDriverSubscription,
        handlePageChange,
        handleSorting,
        getDriverDetails,
    };
};
